/**
 * Per-stage email controls — pause, resume, and the truth about what that does.
 *
 * 🔴 THE CONTROL STOPS A SEND (Task 7.1). `sendStageEmail` reads
 * `core.notification_stage_controls` before it composes anything and returns
 * the NotSent reason `stage_paused`; a queued email for that stage is
 * cancelled. The in-app card and the push are unaffected — this is the email
 * rail only. The page says so ON THE BUTTON ITSELF, not only in a paragraph,
 * because both errors cost the same: an operator who thinks the control is
 * inert reaches for the environment kill switch and stops EVERY stage, and an
 * operator who over-reads it thinks the cards stopped too.
 *
 * The exact words live in `../data.ts` as `PAUSE_CONTROL_LABEL` and
 * `WAVE_7_NOTE` and are RENDERED FROM THERE. A hand-typed second copy in this
 * file could be edited without the test noticing, so the test forbids one.
 *
 * The button renders only for an admin holding `notifications.controls.write`
 * (super_admin). The confirm step is not decoration: a pause needs a reason,
 * because the next person to read this table needs to know why — and the
 * breaker writes its own reason there, with the numbers it judged.
 */
import {
  PAUSE_CONTROL_LABEL,
  WAVE_7_NOTE,
  WAVE_7_NOTE_HEADING,
  formatCount,
  stageLabel,
  tileBody,
  type ControlAction,
  type ControlsData,
  type PendingControlView,
  type Tile,
} from '../data';
import { TileUnavailable, tileShell } from '../../system-health/components/TileStatus';

interface Props {
  tile: Tile<ControlsData>;
  loading: boolean;
  canWrite: boolean;
  pending: PendingControlView | null;
  reason: string;
  submitting: boolean;
  actionError: string | null;
  onOpen: (stage: string, action: ControlAction) => void;
  onClose: () => void;
  onReasonChange: (reason: string) => void;
  onConfirm: () => void;
}

/** Whoever last moved this stage: the pauser while paused, else the resumer. */
function actorId(row: ControlsData['stages'][number]): string | null {
  return row.email_paused ? row.paused_by : (row.resumed_by ?? row.paused_by);
}

function shortActor(id: string | null): string {
  if (!id) return '—';
  return id.length > 10 ? `${id.slice(0, 8)}…` : id;
}

function when(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

export function StageControls({
  tile,
  loading,
  canWrite,
  pending,
  reason,
  submitting,
  actionError,
  onOpen,
  onClose,
  onReasonChange,
  onConfirm,
}: Props) {
  const unavailable = tile.status === 'unavailable';
  const body = tileBody(tile, loading);
  const data = tile.data;
  const rows = data?.stages ?? [];

  return (
    <section className={`border rounded-lg p-6 ${tileShell(unavailable, 'bg-card')}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
        <div>
          <h3 className="text-lg font-semibold">Stage email controls</h3>
          <p className="text-xs text-muted-foreground">
            {body === 'ready' && data
              ? `${formatCount(data.paused_count)} of ${formatCount(rows.length)} stages paused.`
              : 'Per-stage pause and resume.'}
          </p>
        </div>
      </div>

      {/* 🔴 What a pause does, above the controls, from the constant. */}
      <div className="rounded border border-amber-500/50 bg-amber-500/10 p-3 text-sm text-amber-800 my-4">
        <div className="font-medium">{WAVE_7_NOTE_HEADING}</div>
        <div className="text-xs mt-0.5 opacity-90">{WAVE_7_NOTE}</div>
      </div>

      {unavailable && <TileUnavailable error={tile.error} />}

      {body === 'loading' && (
        <p className="text-sm text-muted-foreground">Loading the controls…</p>
      )}

      {body === 'ready' && !canWrite && rows.length > 0 && (
        <p className="mb-3 text-xs text-muted-foreground">
          You can read these controls. Changing one needs the notifications control-write
          permission.
        </p>
      )}

      {body === 'ready' && rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 pr-4 text-left font-medium">Stage</th>
                <th className="px-2 py-2 text-left font-medium">Email</th>
                <th className="px-2 py-2 text-left font-medium">Since</th>
                <th className="px-2 py-2 text-left font-medium">Reason last given</th>
                <th className="px-2 py-2 text-left font-medium">Changed by</th>
                {canWrite && <th className="px-2 py-2 text-right font-medium">Action</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.stage} className="border-b last:border-0">
                  <td className="py-2 pr-4 font-medium whitespace-nowrap">
                    {stageLabel(row.stage)}
                  </td>
                  <td className="px-2 py-2">
                    <span
                      className={`rounded px-2 py-0.5 text-xs ${
                        row.email_paused
                          ? 'bg-amber-500/15 text-amber-800'
                          : 'bg-emerald-500/10 text-emerald-700'
                      }`}
                    >
                      {row.email_paused ? 'Paused' : 'Sending'}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-xs text-muted-foreground whitespace-nowrap">
                    {when(row.email_paused ? row.email_paused_at : row.resumed_at)}
                  </td>
                  <td className="px-2 py-2 text-xs text-muted-foreground">
                    {row.email_pause_reason || '—'}
                  </td>
                  {/* The admin who last moved this stage. A control table whose
                      rows carry no author is a log nobody can follow up on. The
                      full id is in the title; the cell shows a readable stub. */}
                  <td
                    className="px-2 py-2 text-xs text-muted-foreground font-mono"
                    title={actorId(row) ?? undefined}
                  >
                    {shortActor(actorId(row))}
                  </td>
                  {canWrite && (
                    <td className="px-2 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => onOpen(row.stage, row.email_paused ? 'resume' : 'pause')}
                        title={row.email_paused ? 'Resume this stage email' : PAUSE_CONTROL_LABEL}
                        className="rounded border px-2 py-1 text-xs hover:bg-muted"
                      >
                        {row.email_paused ? 'Resume' : 'Pause'}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pending && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="stage-control-title"
            className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-lg bg-card"
          >
            <div className="shrink-0 border-b p-6">
              <h4 id="stage-control-title" className="text-lg font-semibold">
                {pending.action === 'pause'
                  ? PAUSE_CONTROL_LABEL
                  : 'Resume stage email'}
              </h4>
              <p className="mt-1 text-sm text-muted-foreground">
                {stageLabel(pending.stage)}
              </p>
            </div>

            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-6">
              <p className="text-xs text-muted-foreground">{WAVE_7_NOTE}</p>
              <label className="block text-sm font-medium" htmlFor="stage-control-reason">
                Reason{pending.action === 'pause' ? '' : ' (optional)'}
              </label>
              {/* The dialog mounts only when `pending` turns non-null, so the
                  field is fresh every time and autoFocus lands the caret where
                  the operator must type. A pause cannot be confirmed without
                  it, so it is the one control worth focusing. */}
              <textarea
                id="stage-control-reason"
                autoFocus
                value={reason}
                onChange={(e) => onReasonChange(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="Why is this changing? It is written to the audit log."
                className="w-full rounded border bg-background p-2 text-sm"
              />
              {actionError && (
                <p className="text-sm text-destructive" role="alert">
                  {actionError}
                </p>
              )}
            </div>

            <div className="shrink-0 flex justify-end gap-2 border-t p-6">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="rounded border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onConfirm}
                disabled={submitting}
                className="rounded bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50"
              >
                {submitting
                  ? 'Saving…'
                  : pending.action === 'pause'
                    ? 'Pause this stage'
                    : 'Resume this stage'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
